import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { AddEditCustomReportState, getTab } from '../../excel-listings/custom-report-helpers';
import { CoreTabs, ICoreTab } from '@components-clean/core/core-tabs';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import { DragDropListItemProps } from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import { useTranslation } from 'hooks';
import {
    ReportDefinitionFields,
    ReportDefinitionFields_ReportDefinition_tabs_ReportDefinitionTab as ReportDefinitionFields_tabs,
    ReportDefinitionFields_ReportDefinition_tabs_ReportDefinitionTab_columns_ReportDefinitionColumn as ReportDefinitionFields_tabs_columns,
} from 'queries/generated/fragments.generated';
import { ReportDefinitionTabs } from 'types/graphql-global-types';
import { textToId } from '@utils/automation-test-helpers';
import { notEmpty } from '@utils/utility-helpers';

interface CustomReportStep3ColumnsProps {
    show: boolean;
    optionsTabs: ReportDefinitionFields_tabs[];
    reportDefinition: ReportDefinitionFields;
    state: AddEditCustomReportState;
    setState: Dispatch<SetStateAction<AddEditCustomReportState>>;
    resetColumns: (tabType: ReportDefinitionTabs) => void;
}

const CustomReportStep3Columns = ({
    show,
    optionsTabs,
    reportDefinition,
    state,
    setState,
    resetColumns,
}: CustomReportStep3ColumnsProps) => {
    const { t } = useTranslation();
    const { t: tDialog } = useTranslation('ram', {
        keyPrefix: 'reporting.excelListings.dialogs.addEditExcelListingsConfig',
    });
    const [tabNumber, setTabNumber] = useState(0);
    const currentLeftItemsRef = useRef<DragDropListItemProps[]>([]);
    const currentRightItemsRef = useRef<DragDropListItemProps[]>([]);
    const currentSelectedItemRef = useRef<DragDropListItemProps>(null);

    const getCurrentTabType = () => {
        if (!reportDefinition.tabs) return undefined;

        return reportDefinition.tabs[tabNumber]?.tabType;
    };

    const leftListActiveItems = currentLeftItemsRef.current.filter((i) => i.selected);
    const rightListActiveItems = currentRightItemsRef.current.filter((i) => i.selected);

    const getListItems = (inputColumns: (ReportDefinitionFields_tabs_columns | null)[] | null | undefined) => {
        if (!inputColumns) return [] as DragDropListItemProps[];

        return inputColumns.filter(notEmpty).map(
            (col) =>
                ({
                    key: col.dataField,
                    title: t(`reports:${col.dataField}`),
                    selected:
                        leftListActiveItems.find((Item) => Item.key === col.dataField) !== undefined ||
                        rightListActiveItems.find((Item) => Item.key === col.dataField) !== undefined,
                }) as DragDropListItemProps,
        );
    };

    const getColumns = (listItems: DragDropListItemProps[]) =>
        listItems.map(
            (listItem) =>
                ({
                    __typename: 'ReportDefinitionColumn',
                    dataField: listItem.key,
                    index: 0,
                }) as ReportDefinitionFields_tabs_columns,
        );

    const getNotSelectedColumns = () => {
        const optionsTab = getTab(optionsTabs, getCurrentTabType());

        if (!optionsTab) return [] as ReportDefinitionFields_tabs_columns[];

        return optionsTab.columns;
    };

    const getUpdatedReportDefinitionTabs = (
        tabsToUpdate: ReportDefinitionFields_tabs[],
        columns: ReportDefinitionFields_tabs_columns[],
    ) => {
        const updatedTabs = [...tabsToUpdate];

        const tab = getTab(tabsToUpdate, getCurrentTabType());

        if (tab) tab.columns = [...columns];

        return updatedTabs;
    };

    const update = (list1Items: DragDropListItemProps[], list2Items: DragDropListItemProps[]) => {
        const updatedReportDefinitionTabs = getUpdatedReportDefinitionTabs(
            reportDefinition.tabs?.filter(notEmpty) ?? [],
            getColumns(list2Items),
        );
        const updatedOptionsTabs = getUpdatedReportDefinitionTabs(optionsTabs, getColumns(list1Items));

        setState({
            ...state,
            reportDefinition: {
                ...state.reportDefinition,
                tabs: updatedReportDefinitionTabs,
            },
            optionsTabs: updatedOptionsTabs,
        });
    };

    const reset = () => {
        const tabType = getCurrentTabType();
        if (!tabType) return;
        resetColumns(tabType);
    };

    const getTabs: ICoreTab[] =
        reportDefinition.tabs
            ?.filter((tab) => tab !== null)
            .map((tab: ReportDefinitionFields_tabs, index) => ({
                tabTitle: t(`reports:${tab.tabType}`),
                key: index,
                tabContent: (
                    <div style={{ height: '425px', padding: '6px 0px' }} key={`test-${tab.tabType}`}>
                        <DragDropListsCore
                            list1Items={getListItems(
                                reportDefinition.tabs ? reportDefinition.tabs[tabNumber]?.columns : null,
                            )}
                            list2Items={getListItems(getNotSelectedColumns())}
                            list1Header={tDialog('steps.3.selected')}
                            list2Header={tDialog('steps.3.available')}
                            onChangeItemsLeft={(items) => {
                                currentLeftItemsRef.current = items;
                            }}
                            onChangeItemsRight={(items) => {
                                currentRightItemsRef.current = items;
                                update(items, [...currentLeftItemsRef.current]);
                            }}
                            idField="key"
                            isTwoLists
                            handleReset={reset}
                            optionalItemClickCallback={(event) => {
                                currentSelectedItemRef.current = event.dataItem;
                            }}
                        />
                    </div>
                ),
                dataTestId: `tab-${textToId(tab.tabType!, index)}`,
            })) ?? [];

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setTabNumber(0);
    }, [show]);

    return (
        <>
            {show && (
                <div className="step3-columns k-p-3">
                    <CoreTabs
                        tabsData={getTabs}
                        scrollable
                        activeTab={tabNumber}
                        setActiveTab={(index) => {
                            currentSelectedItemRef.current = null;
                            setTabNumber(index);
                        }}
                        data-testid="excel-listings--config-columns-tabs"
                        type="page"
                    />
                </div>
            )}
        </>
    );
};

export default CustomReportStep3Columns;
